import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import cn from '@/utils/cn';
import Icon from '@/components/ui/Icon';
import SPAPopover from '../SPAPopover';
import { useProgramOptions } from './usePrograms';
import useProgramTree, { TreeLesson, TreeUnit } from './useProgramTree';

interface NodeCrumbsProps {
  // The lesson the page is on, or none on a unit or program page.
  lessonId?: number;
  programId: number;
  programTitle: string;
  // Where picking a lesson lands (the same kind of page for that lesson) and where picking a
  // program lands (its table of contents). Both mount-aware, so the page supplies them.
  toLesson: (lessonId: number) => string;
  toProgram: (programId: number) => string;
  // Where picking a unit lands: the unit-level counterpart of this page when it has one;
  // otherwise the unit's first lesson.
  toUnit?: (unitId: number) => string;
  // The unit the page is on; a lesson page derives it from the lesson. A program page passes
  // neither and gets the program crumb alone, as the current node.
  unitId?: number;
}

const crumbClass = cn(
  'tw-inline-flex tw-items-center tw-gap-1 tw-rounded tw-px-1.5 tw-py-0.5 tw-text-xs',
  'tw-text-spa-secondary tw-no-underline hover:tw-bg-spa-bg-tertiary hover:tw-text-spa-on-bg',
);

const currentClass = 'tw-font-bold tw-text-spa-heading';

const switcherClass = cn(
  crumbClass,
  'tw-cursor-pointer tw-border-0 tw-bg-transparent',
  'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-spa-accent',
);

const searchClass = cn(
  'tw-mb-1 tw-box-border tw-w-full tw-rounded-md tw-border tw-border-solid',
  'tw-border-spa-border-input tw-bg-spa-bg-tertiary tw-px-3 tw-py-2 tw-text-sm',
  'tw-text-spa-on-bg tw-outline-none tw-placeholder-spa-secondary',
  'focus:tw-border-spa-accent focus:tw-ring-1 focus:tw-ring-spa-accent',
);

// One line of a crumb's menu: a program, a unit (the group head of its lessons), or a lesson.
interface Row {
  current: boolean;
  id: string;
  kind: 'lesson' | 'program' | 'unit';
  label: string;
  // The unit a lesson row belongs to, so a search that matches the unit keeps its lessons.
  parentId?: string;
  to: string | null;
}

const rowClass = (row: Row, active: boolean) => cn(
  'tw-block tw-rounded-md tw-px-2 tw-py-1.5 tw-text-sm tw-no-underline hover:tw-no-underline',
  row.kind === 'lesson' && 'tw-pl-7 tw-text-[0.8125rem]',
  row.kind === 'unit' && 'tw-font-bold',
  row.current ? 'tw-text-spa-accent-text' : 'tw-text-spa-primary',
  row.to ? 'tw-cursor-pointer' : 'tw-cursor-not-allowed tw-text-spa-tertiary',
  active && 'tw-bg-spa-bg-hover tw-text-spa-on-bg tw-ring-2 tw-ring-inset tw-ring-spa-accent',
);

const Separator = () => <span aria-hidden="true" className="tw-text-spa-tertiary">/</span>;

const lessonLabel = (lesson: TreeLesson, long = false) => {
  if (!lesson.title_number) return lesson.title;

  return long ? `Lesson ${lesson.title_number} · ${lesson.title}` : `Lesson ${lesson.title_number}`;
};

const unitCrumbLabel = (unit: TreeUnit | undefined, long: boolean) => {
  if (!unit) return 'Unitless lessons';

  return long ? `Unit ${unit.display_number} · ${unit.title}` : `Unit ${unit.display_number}`;
};

interface CrumbSwitcherProps {
  ariaLabel: string;
  children: React.ReactNode;
  // The row the panel opens on: this crumb's own node.
  focusId: string;
  rows: Row[];
  searchPlaceholder: string;
  strong?: boolean;
}

// A crumb that opens a searchable panel: every program, or the whole program's units and
// lessons. Arrow keys move the highlight, Enter goes there, Escape closes; the panel opens on
// the crumb's own node.
const CrumbSwitcher = ({
  ariaLabel, children, focusId, rows, searchPlaceholder, strong = false,
}: CrumbSwitcherProps) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState(focusId);
  const activeRef = useRef<HTMLAnchorElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  // A hover highlight must not scroll the list: a half-clipped row would jump under the mouse.
  const hoverHighlightRef = useRef(false);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;

    const matches = (row: Row) => row.label.toLowerCase().includes(needle);
    const matchingHeads = new Set(rows.filter(row => row.kind !== 'lesson' && matches(row)).map(row => row.id));
    const headsWithMatchingLessons = new Set(
      rows.filter(row => row.kind === 'lesson' && matches(row) && row.parentId).map(row => row.parentId as string),
    );

    return rows.filter(row => (
      row.kind === 'lesson'
        ? matches(row) || (row.parentId !== undefined && matchingHeads.has(row.parentId))
        : matchingHeads.has(row.id) || headsWithMatchingLessons.has(row.id)
    ));
  }, [query, rows]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveId(focusId);
  }, [open, focusId]);

  // A filtered-out highlight moves to the first result.
  useEffect(() => {
    if (visible.length > 0 && !visible.some(row => row.id === activeId)) setActiveId(visible[0].id);
  }, [activeId, visible]);

  // Keep the highlighted row in view after keyboard moves and on open. The popover is positioned
  // a frame after it mounts, so the scroll is deferred; it is done by hand against the list
  // because scrollIntoView would also scroll the page to the popover.
  useEffect(() => {
    if (!open) return undefined;
    if (hoverHighlightRef.current) {
      hoverHighlightRef.current = false;
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => {
      const list = listRef.current;
      const row = activeRef.current;
      if (!list || !row) return;

      const rowTop = row.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
      const rowBottom = rowTop + row.offsetHeight;
      if (rowTop < list.scrollTop) list.scrollTop = rowTop - 4;
      else if (rowBottom > list.scrollTop + list.clientHeight) list.scrollTop = rowBottom - list.clientHeight + 4;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeId, open]);

  const go = (row: Row | undefined) => {
    if (!row?.to) return;
    setOpen(false);
    navigate(row.to);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const index = visible.findIndex(row => row.id === activeId);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveId(visible[(index + 1) % visible.length]?.id ?? activeId);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveId(visible[(index - 1 + visible.length) % visible.length]?.id ?? activeId);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      go(visible[index]);
    }
  };

  return (
    <SPAPopover open={open} onOpenChange={setOpen}>
      <SPAPopover.Trigger asChild>
        <button
          aria-label={ariaLabel}
          className={cn(switcherClass, strong && currentClass)}
          type="button"
        >
          {children}
          <Icon icon="ChevronDown" size={12} />
        </button>
      </SPAPopover.Trigger>
      <SPAPopover.Content align="start" className="tw-w-80 tw-p-2">
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          aria-label={`Search ${ariaLabel.replace('Switch ', '')}s`}
          className={searchClass}
          placeholder={searchPlaceholder}
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <div ref={listRef} aria-label="Program menu" className="tw-max-h-72 tw-overflow-y-auto" role="group">
          {visible.map(row => (row.to ? (
            <Link
              key={row.id}
              ref={row.id === activeId ? activeRef : undefined}
              aria-current={row.current ? 'page' : undefined}
              className={rowClass(row, row.id === activeId)}
              data-active={row.id === activeId || undefined}
              to={row.to}
              onClick={() => setOpen(false)}
              onMouseEnter={() => {
                hoverHighlightRef.current = true;
                setActiveId(row.id);
              }}
            >
              {row.label}
            </Link>
          ) : (
            <span key={row.id} className={rowClass(row, false)}>
              {row.label}
              <span className="tw-ml-2 tw-text-xs tw-font-normal tw-text-spa-tertiary">no lessons</span>
            </span>
          )))}
          {visible.length === 0 && (
            <p className="tw-m-0 tw-px-2 tw-py-1.5 tw-text-sm tw-text-spa-secondary">No matches.</p>
          )}
        </div>
      </SPAPopover.Content>
    </SPAPopover>
  );
};

// AIDEV-NOTE: The path above every content page — program ▾ / unit ▾ / lesson ▾ — where every
// crumb opens a searchable panel: the program crumb lists the programs (the rail switcher's set,
// landing on a table of contents), the unit and lesson crumbs the whole program (units, lessons
// indented), the way the legacy Unit/Lesson select listed it. Each panel opens on the crumb's own
// node. A unit lands on the page's unit counterpart (`toUnit`) or its first lesson; a lesson on
// the same kind of page (`toLesson`); a unit page omits the lesson crumb, a program page both.
// The last crumb is the page's node, bold and with its title, since the header names only the
// page. Data is the rail's tree and program-summary queries.
const NodeCrumbs = ({
  lessonId, programId, programTitle, toLesson, toProgram, toUnit, unitId,
}: NodeCrumbsProps) => {
  const { allLessons, ghostLessons, hasUnits, units } = useProgramTree(programId);
  const { options: programOptions } = useProgramOptions();
  const lesson = lessonId === undefined ? undefined : allLessons.find(l => l.id === lessonId);
  const unit = units.find(u => u.id === (unitId ?? lesson?.unit_id));
  const onProgram = unitId === undefined && lessonId === undefined;

  // The switcher's curated set, plus this program when it is outside it (custom or archived).
  const programRows = useMemo<Row[]>(() => {
    const programRow = (id: number, label: string): Row => ({
      current: id === programId, id: `program-${id}`, kind: 'program', label, to: toProgram(id),
    });
    const listed = programOptions.map(option => programRow(Number(option.value), option.label));
    if (listed.some(row => row.current) || !programTitle) return listed;

    return [programRow(programId, programTitle), ...listed];
  }, [programId, programOptions, programTitle, toProgram]);

  const rows = useMemo<Row[]>(() => {
    const lessonRow = (candidate: TreeLesson, parentId?: string): Row => ({
      current: candidate.id === lessonId,
      id: `lesson-${candidate.id}`,
      kind: 'lesson',
      label: lessonLabel(candidate, true),
      parentId,
      to: toLesson(candidate.id),
    });

    if (!hasUnits) return allLessons.map(candidate => lessonRow(candidate));

    const unitRows = units.flatMap((candidate) => {
      const id = `unit-${candidate.id}`;
      let to: string | null = null;
      if (toUnit) to = toUnit(candidate.id);
      else if (candidate.lessons[0]) to = toLesson(candidate.lessons[0].id);

      const unitRow: Row = {
        current: candidate.id === unit?.id,
        id,
        kind: 'unit',
        label: `Unit ${candidate.display_number} · ${candidate.title}`,
        to,
      };

      return [
        unitRow,
        ...candidate.lessons.map(l => lessonRow(l, id)),
      ];
    });
    if (ghostLessons.length === 0) return unitRows;

    return [
      ...unitRows,
      {
        current: !unit && lessonId !== undefined,
        id: 'unit-0',
        kind: 'unit',
        label: 'Unitless lessons',
        to: toLesson(ghostLessons[0].id),
      },
      ...ghostLessons.map(l => lessonRow(l, 'unit-0')),
    ];
  }, [allLessons, ghostLessons, hasUnits, lessonId, toLesson, toUnit, unit, units]);

  return (
    <nav aria-label="Breadcrumb" className="tw-mb-2 tw-flex tw-flex-wrap tw-items-center tw-gap-1">
      <CrumbSwitcher
        ariaLabel="Switch program"
        focusId={`program-${programId}`}
        rows={programRows}
        searchPlaceholder="Search programs…"
        strong={onProgram}
      >
        {programTitle}
      </CrumbSwitcher>

      {!onProgram && hasUnits && (
        <>
          <Separator />
          <CrumbSwitcher
            ariaLabel="Switch unit"
            focusId={`unit-${unit?.id ?? 0}`}
            rows={rows}
            searchPlaceholder="Search units and lessons…"
            strong={!lesson}
          >
            {unitCrumbLabel(unit, !lesson)}
          </CrumbSwitcher>
        </>
      )}

      {lesson && (
        <>
          <Separator />
          <CrumbSwitcher
            strong
            ariaLabel="Switch lesson"
            focusId={`lesson-${lesson.id}`}
            rows={rows}
            searchPlaceholder="Search units and lessons…"
          >
            {lessonLabel(lesson, true)}
          </CrumbSwitcher>
        </>
      )}
    </nav>
  );
};

export default NodeCrumbs;
